import React from 'react';
import { cn } from '@/lib/utils';
import { Image } from '@/pages/reports/components/ui';
import { HtmlContent } from '@/components/ui/html-content';
import { useReportI18n } from '@/pages/reports/i18n';

interface HighlightItem {
  id?: string | number;
  title: string;
  titleLink?: string;
  description: string;
}

interface HighlightsCardProps {
  subtitle?: string;
  illustration?: React.ReactNode | string;
  items: HighlightItem[];
  className?: string;
}

const hasIllustration = (ill?: React.ReactNode | string) => typeof ill === 'string' ? ill.trim() !== '' : ill != null && typeof ill !== 'string';

const IllustrationContent: React.FC<{ illustration?: React.ReactNode | string }> = ({
  illustration
}) => {
  if (!hasIllustration(illustration)) return null;
  if (typeof illustration === 'string') {
    return (
      <Image
        src={illustration.trim()}
        alt=""
        className="w-full h-[198px] object-cover rounded-[18px]"
      />
    );
  }
  return <>{illustration}</>;
};

export const HighlightsCard: React.FC<HighlightsCardProps> = ({
  subtitle,
  illustration,
  items,
  className
}) => {
  const { t } = useReportI18n();

  if (!items.length) {
    return null;
  }

  return (
    <section className={cn('w-full bg-black text-white', className)}>
      <div className="container mx-auto">
        <div className="w-full max-w-[500px] mx-auto rounded-[40px] py-10 md:py-14">
          <div className="flex flex-col gap-10 md:gap-14">
            <div className="flex flex-col gap-8 md:gap-10 items-center text-center">
              <div className="space-y-4 max-w-3xl">
                <h2 className="text-[clamp(2.75rem,11vw,5rem)] leading-[1.05] font-['Times_New_Roman'] text-balance break-words">
                  {t('highlights.title')}
                </h2>
                {subtitle && (
                  <HtmlContent
                    html={subtitle}
                    className="text-[14px] text-[#D9D9D9]"
                  />
                )}
              </div>
              {hasIllustration(illustration) && (
                <div className="flex md:justify-center w-full">
                  <IllustrationContent illustration={illustration} />
                </div>
              )}
            </div>
            <div className="flex flex-row gap-8 overflow-x-auto snap-x snap-mandatory pb-2">
              {items.map((item, index) => (
                <div
                  key={item.id ?? `${item.title}-${index}`}
                  className="flex flex-col gap-3 snap-start shrink-0 w-[min(100%,420px)]"
                >
                  <h3 className="font-bold text-[36px] leading-[40px] tracking-tight">
                    {item.titleLink?.trim() ? (
                      <a
                        href={item.titleLink.trim()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        {item.title}
                      </a>
                    ) : (
                      item.title
                    )}
                  </h3>
                  <HtmlContent
                    html={item.description}
                    className="font-normal text-sm leading-5 tracking-normal text-[#E0E0E0]"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
